import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Coins, ArrowCounterClockwise, Check } from "@phosphor-icons/react";

export default function AdminRates() {
  const qc = useQueryClient();
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState("");
  const { data } = useQuery({ queryKey: ["calling-rates"], queryFn: async () => (await api.get("/admin/calling-rates")).data });
  const rates = data?.rates || [];

  const save = async (code) => {
    const cents = parseInt(edits[code], 10);
    if (!cents || cents < 1) { toast.error("Enter a price of at least 1¢/min"); return; }
    setSaving(code);
    try {
      await api.put(`/admin/calling-rates/${code}`, { retail_cpm: cents });
      toast.success("Rate saved");
      setEdits((e) => { const n = { ...e }; delete n[code]; return n; });
      qc.invalidateQueries({ queryKey: ["calling-rates"] });
    } catch { toast.error("Could not save rate"); } finally { setSaving(""); }
  };

  const reset = async (code) => {
    setSaving(code);
    try {
      await api.delete(`/admin/calling-rates/${code}`);
      toast.success("Reset to default");
      setEdits((e) => { const n = { ...e }; delete n[code]; return n; });
      qc.invalidateQueries({ queryKey: ["calling-rates"] });
    } catch { toast.error("Could not reset"); } finally { setSaving(""); }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-rates">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Calling Rates</h1>
        <p className="mt-1 text-sm text-muted-foreground">Set your own retail price per country (cents per minute). Changes apply to new calls immediately — your margin is the difference over wholesale.</p>
      </div>

      <div className="overflow-hidden rounded-2xl border border-white/10 bg-card">
        <div className="grid grid-cols-[1fr_auto_auto] items-center gap-3 border-b border-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <span>Destination</span><span>¢ / min</span><span></span>
        </div>
        {rates.map((r) => {
          const val = edits[r.code] ?? String(r.retail_cpm);
          const num = parseInt(edits[r.code], 10);
          const valid = Number.isInteger(num) && num >= 1 && num <= 1000;
          const dirty = edits[r.code] !== undefined && valid && num !== r.retail_cpm;
          return (
            <div key={r.code} data-testid={`rate-row-${r.code}`} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 border-b border-white/5 px-4 py-3 last:border-0">
              <div className="flex items-center gap-2">
                <span className="text-lg">{r.flag}</span>
                <div>
                  <p className="text-sm font-medium">{r.name}</p>
                  <p className="text-xs text-muted-foreground">{r.dial}{r.overridden ? " · custom" : " · default"}</p>
                </div>
              </div>
              <input data-testid={`rate-input-${r.code}`} type="number" min="1" max="1000" value={val}
                onChange={(e) => setEdits((x) => ({ ...x, [r.code]: e.target.value }))}
                className="w-20 rounded-lg border border-white/10 bg-background px-3 py-1.5 text-right text-sm outline-none focus:border-primary" />
              <div className="flex items-center gap-1.5">
                <button data-testid={`rate-save-${r.code}`} onClick={() => save(r.code)} disabled={!dirty || saving === r.code}
                  className="flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground disabled:opacity-40"><Check size={13} weight="bold" /> Save</button>
                {r.overridden && (
                  <button data-testid={`rate-reset-${r.code}`} onClick={() => reset(r.code)} disabled={saving === r.code}
                    title="Reset to default" className="rounded-lg border border-white/10 p-1.5 text-muted-foreground hover:text-foreground"><ArrowCounterClockwise size={14} /></button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Coins size={13} /> Tip: "International (all other countries)" is the price for any destination not listed above.</p>
    </div>
  );
}
